import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { avatarUrl } from '../../utils/avatar';
import { LEVEL_ICONS } from '../WellnessJourneyCard';

/**
 * MobileTopBar — app-style header (mobile/tablet only; desktop keeps the
 * sidebar). Left: Revivo IQ wordmark (taps to Home). Right: live Journey
 * level + XP chip (taps to /journey) and an avatar that opens the "You"
 * overflow sheet. Home / Journey / You all live here now that the bottom bar
 * is reserved for the four pillars + Nikki.
 */
const MobileTopBar = ({ onYouClick }) => {
  const [j, setJ] = useState(null);
  const { user, token } = useAuth();
  const photo = avatarUrl(user, token);

  useEffect(() => {
    let mounted = true;
    api.getJourney().then((r) => { if (mounted) setJ(r.data); }).catch(() => {});
    const refresh = () => api.getJourney().then((r) => setJ(r.data)).catch(() => {});
    window.addEventListener('wellness:refresh', refresh);
    return () => { mounted = false; window.removeEventListener('wellness:refresh', refresh); };
  }, []);

  const LvIcon = j ? (LEVEL_ICONS[j.level.index] || Activity) : null;

  return (
    <header
      className="lg:hidden fixed top-0 inset-x-0 z-40 hpme-glass border-b border-white/5 safe-top"
      data-testid="mobile-top-bar"
    >
      <div className="h-16 pt-2.5 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="mobile-logo">
          <div className="w-9 h-9 bg-primary rounded-md flex items-center justify-center">
            <Activity className="w-5 h-5 text-black" />
          </div>
          <span className="text-lg font-secondary font-bold tracking-tight">Revivo<span className="text-primary">&nbsp;IQ</span></span>
        </Link>

        <div className="flex items-center gap-2.5">
          {j && (
            <Link
              to="/journey"
              className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 pl-2 pr-3 py-1.5 active:scale-95 transition-transform"
              data-testid="mobile-level-chip"
            >
              {LvIcon && <LvIcon className="w-4 h-4 text-primary" />}
              <div className="flex flex-col items-start">
                <span className="text-[11px] font-secondary font-bold text-white leading-none">Lv {j.level.index + 1} · <span className="text-primary">{j.total_xp} XP</span></span>
                {!j.level.is_max && (
                  <span className="mt-1 block w-12 h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <span className="block h-full bg-primary" style={{ width: `${j.level.pct}%` }} />
                  </span>
                )}
              </div>
            </Link>
          )}

          <button
            type="button"
            onClick={onYouClick}
            className="w-9 h-9 rounded-full bg-zinc-800 overflow-hidden border border-white/10 flex items-center justify-center text-sm font-bold text-white active:scale-95 transition-transform"
            data-testid="mobile-you-trigger"
            aria-label="You menu"
          >
            {photo ? <img src={photo} alt="You" className="w-full h-full object-cover" /> : (user?.name?.charAt(0) || 'U')}
          </button>
        </div>
      </div>
    </header>
  );
};

export default MobileTopBar;
